import React, { useState, useEffect } from 'react';
import { Heart, Edit2, Share2, Languages } from 'lucide-react';
import ShareQuote from './ShareQuote';
import { translateText, getDeviceLanguage } from '../utils/translationUtils';

type Quote = {
  id: number;
  text: string;
  author: string;
  date: string;
};

type FavoriteQuote = Quote & {
  collectedAt: number;
  insight?: string;
};

type QuoteInteractionProps = {
  quote: Quote;
  onLike: (quote: Quote) => void;
  favorites: FavoriteQuote[];
  onUpdateInsight: (id: number, insight: string) => void;
};

const QuoteInteraction: React.FC<QuoteInteractionProps> = ({ quote, onLike, favorites, onUpdateInsight }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [showInsightInput, setShowInsightInput] = useState(false);
  const [insight, setInsight] = useState('');
  const [existingInsight, setExistingInsight] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [isTranslated, setIsTranslated] = useState(false);
  const [translatedText, setTranslatedText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    const favorite = favorites.find(fav => fav.id === quote.id);
    setIsLiked(!!favorite);
    setExistingInsight(favorite?.insight || '');
    // Reset translation state when quote changes
    setIsTranslated(false);
    setTranslatedText('');
  }, [favorites, quote]);

  const handleLike = () => {
    const newLikedState = !isLiked;
    onLike(quote);
    setIsLiked(newLikedState);
    if (newLikedState && !existingInsight.trim()) {
      setShowInsightInput(true);
    }
  };

  const handleInsightSubmit = () => {
    const trimmedInsight = insight.trim();
    onUpdateInsight(quote.id, trimmedInsight);
    setShowInsightInput(false);
    setExistingInsight(trimmedInsight);
    setInsight('');
  };

  const handleEditInsight = () => {
    setShowInsightInput(!showInsightInput);
    setInsight(existingInsight);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleTranslate = async () => {
    if (!isTranslated) {
      setIsTranslating(true);
      try {
        const deviceLanguage = getDeviceLanguage();
        const translated = await translateText(quote.text, deviceLanguage);
        if (translated) {
          setTranslatedText(translated);
          setIsTranslated(true);
        }
      } catch (error) {
        console.error('Translation error:', error);
      } finally {
        setIsTranslating(false);
      }
    } else {
      setIsTranslated(false);
    }
  };

  return (
    <div className="w-full">
      <div className="text-sm text-indigo-600 font-semibold mb-2">{quote.date}</div>
      <blockquote className="text-xl italic mb-4">
        "{isTranslated ? translatedText : quote.text}"
      </blockquote>
      <p className="text-right text-lg text-indigo-600 font-semibold mb-4">- {quote.author}</p>
      <div className="flex justify-between items-center mb-2">
        <div className="flex space-x-2">
          <button
            onClick={handleShare}
            className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-blue-500 hover:text-white transition-colors duration-200"
            title="Share Quote"
          >
            <Share2 size={20} />
          </button>
          <button
            onClick={handleTranslate}
            className={`p-2 rounded-full transition-colors duration-200 ${
              isTranslated ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
            } hover:bg-green-600 hover:text-white`}
            title={isTranslated ? "Show Original" : "Translate Quote"}
            disabled={isTranslating}
          >
            <Languages size={20} />
          </button>
        </div>
        <button
          onClick={handleLike}
          className={`p-2 rounded-full transition-colors duration-200 ${
            isLiked ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-600'
          } hover:bg-red-600 hover:text-white`}
          title={isLiked ? "Unlike" : "Like"}
        >
          <Heart size={20} fill={isLiked ? "white" : "none"} />
        </button>
      </div>
      {isLiked && (
        <div className="flex justify-end mb-4">
          <button
            onClick={handleEditInsight}
            className={`p-2 rounded-full ${showInsightInput ? 'bg-blue-500 text-white' : 'bg-gray-200 text-blue-500'} hover:bg-blue-600 hover:text-white transition-colors duration-200`}
            title={showInsightInput ? "Close Insight" : "Edit Insight"}
          >
            <Edit2 size={20} />
          </button>
        </div>
      )}
      {isLiked && (
        <div className="w-full mt-4">
          {existingInsight.trim() && !showInsightInput && (
            <p className="text-sm text-gray-600 italic mb-2">Insight: {existingInsight}</p>
          )}
          {showInsightInput && (
            <div className="w-full">
              <textarea
                className="w-full p-2 border rounded"
                rows={4}
                placeholder="Write your insight..."
                value={insight}
                onChange={(e) => setInsight(e.target.value)}
              ></textarea>
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleInsightSubmit}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors duration-200"
                >
                  Save Insight
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {showShareModal && (
        <ShareQuote
          quote={{
            ...quote,
            insight: existingInsight
          }}
          isTranslated={isTranslated}
          translatedText={translatedText}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default QuoteInteraction;